import { HOST, API } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'

export const fetchBoundaries = async ({ commit, rootState }, { west, south, east, north, zoom }) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return

  commit('SET_LOADING', true)
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/boundaries`,
      params: { west, south, east, north, zoom },
    })
    commit('SET_BOUNDARIES', response.data)
  } catch (e) {
    console.error('Failed to fetch boundaries:', e)
  } finally {
    commit('SET_LOADING', false)
  }
}

// Bulk lookup by ids for the project list previews. Returns the features instead
// of committing them, so the map's viewport-scoped boundaries stay untouched.
export const fetchBoundariesByIds = async ({ rootState }, projectIds) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId || !projectIds?.length) return []

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/boundaries`,
      // Bounds are required by the endpoint but ignored when project_ids is set.
      params: { west: -180, south: -90, east: 180, north: 90, zoom: 8, project_ids: projectIds.join(',') },
    })
    return response.data?.features || []
  } catch (e) {
    console.error('Failed to fetch project boundaries:', e)
    return []
  }
}

export const fetchProjectBoundary = async ({ commit, rootState }, projectId) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
    })
    commit('SET_CURRENT_BOUNDARY', response.data)
    return response.data
  } catch (e) {
    if (e.response?.status === 404) {
      commit('SET_CURRENT_BOUNDARY', null)
      return null
    }
    console.error('Failed to fetch project boundary:', e)
    return null
  }
}

export const createBoundary = async ({ commit, rootState }, { projectId, geometry, color, placeDetails }) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return null

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
      data: {
        geometry: geometry || null,
        color: color || '#0059ff',
        place_details: placeDetails || null,
      },
    })
    commit('SET_CURRENT_BOUNDARY', response.data)
    return response.data
  } catch (e) {
    if (e.response?.status === 409) {
      // Boundary already exists, update instead
      return await updateBoundary({ commit, rootState }, { projectId, geometry, color, placeDetails })
    }
    const message = e.response?.data?.detail || "Can't create boundary"
    toast({ title: message, variant: 'destructive' })
    return null
  }
}

export const updateBoundary = async ({ commit, rootState }, { projectId, geometry, color, placeDetails }) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return null

  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
      data: {
        geometry: geometry || null,
        color: color || null,
        place_details: placeDetails || null,
      },
    })
    commit('SET_CURRENT_BOUNDARY', response.data)
    return response.data
  } catch (e) {
    const message = e.response?.data?.detail || "Can't update boundary"
    toast({ title: message, variant: 'destructive' })
    return null
  }
}

export const deleteBoundary = async ({ commit, rootState }, projectId) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return false

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/project/${projectId}/boundary`,
    })
    commit('SET_CURRENT_BOUNDARY', null)
    return true
  } catch (e) {
    const message = e.response?.data?.detail || "Can't delete boundary"
    toast({ title: message, variant: 'destructive' })
    return false
  }
}

